package org.matthewo.pages.event.tabs

import emotion.css.ClassName
import emotion.react.css
import js.objects.buildReadonlyRecord
import js.objects.unsafeJso
import org.matthewo.GAME_SPECIFIC_SEARCH
import org.matthewo.YEAR_PARAM
import org.matthewo.api.*
import org.matthewo.components.TeamOption
import org.matthewo.components.TeamSearch
import org.matthewo.js.toFixed
import org.matthewo.pages.event.EVENT_CODE_PARAM
import org.matthewo.pages.event.EVENT_PATH
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.recharts.cartesian.Bar
import org.matthewo.recharts.cartesian.CartesianGrid
import org.matthewo.recharts.cartesian.XAxis
import org.matthewo.recharts.cartesian.YAxis
import org.matthewo.recharts.chart.BarChart
import org.matthewo.recharts.component.Label
import org.matthewo.recharts.component.Tooltip
import org.matthewo.recharts.container.Legend
import org.matthewo.recharts.container.ResponsiveContainer
import org.matthewo.recharts.type.*
import org.matthewo.router.contains
import org.matthewo.router.div
import org.matthewo.routes.API
import org.matthewo.styles.*
import react.*
import react.dom.events.PointerType
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.dom.svg.ReactSVG.line
import react.dom.svg.ReactSVG.svg
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import tanstack.router.core.RoutePath
import tanstack.router.core.ToOptions
import web.cssom.*
import web.dom.document
import web.window.window
import kotlin.math.hypot
import kotlin.math.max

val EVENT_COMPARE_TEAMS_PATH = RoutePath(EVENT_PATH / "compare")

fun ToOptions.toCompareTeams(year: Int? = null, eventCode: String? = null, red: List<Int>, blue: List<Int>, gameSpecific: Boolean = false) {
    to = EVENT_COMPARE_TEAMS_PATH
    params = buildReadonlyRecord {
        if (null != year) this[YEAR_PARAM] = year.toString()
        if (null != eventCode) this[EVENT_CODE_PARAM] = eventCode
    }
    search = buildReadonlyRecord {
        this["red"] = red.joinToString(",")
        this["blue"] = blue.joinToString(",")
        if (gameSpecific && year in GAME_SPECIFIC_CONFIGS) this[GAME_SPECIFIC_SEARCH] = Unit
    }
}

fun ToOptions.toCompareTeams(year: Int? = null, eventCode: String? = null, match: Schedule.Match) =
    toCompareTeams(year, eventCode, match.red.teams.map { it.number }, match.blue.teams.map { it.number })

private val GAME_SPECIFIC_CONFIGS = mapOf(
    -1 to [
        BoxPlotSeries("NP Total", TOTAL_COLOR.chart, ComparisonStats::npTotal),
        BoxPlotSeries("Auto", AUTO_COLOR.chart, ComparisonStats::auto),
        BoxPlotSeries("Teleop", TELEOP_COLOR.chart, ComparisonStats::teleop),
        BoxPlotSeries("Endgame", ENDGAME_COLOR.chart, ComparisonStats::endgame),
    ],
    2021 to [
        BoxPlotSeries("Alliance", AUTO_COLOR.chart) { (it.gameSpecific as? FreightFrenzyComparisonStats)?.alliance ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Shared", TELEOP_COLOR.chart) { (it.gameSpecific as? FreightFrenzyComparisonStats)?.shared ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Carousel", ENDGAME_COLOR.chart) { (it.gameSpecific as? FreightFrenzyComparisonStats)?.carousel ?: EMPTY_COMPARISON_STAT },
    ],
    102021 to [
        BoxPlotSeries("Alliance", AUTO_COLOR.chart) { (it.gameSpecific as? FreightFrenzyCRIComparisonStats)?.alliance ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Shared", TELEOP_COLOR.chart) { (it.gameSpecific as? FreightFrenzyCRIComparisonStats)?.shared ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Coop", ENDGAME_COLOR.chart) { (it.gameSpecific as? FreightFrenzyCRIComparisonStats)?.coop ?: EMPTY_COMPARISON_STAT },
    ],
    2024 to [
        BoxPlotSeries("Specimen", AUTO_COLOR.chart) { (it.gameSpecific as? IntoTheDeepComparisonStats)?.specimen ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Sample", TELEOP_COLOR.chart) { (it.gameSpecific as? IntoTheDeepComparisonStats)?.sample ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Navigation", ENDGAME_COLOR.chart) { (it.gameSpecific as? IntoTheDeepComparisonStats)?.navigation ?: EMPTY_COMPARISON_STAT },
    ],
    102024 to [
        BoxPlotSeries("Specimen", AUTO_COLOR.chart) { (it.gameSpecific as? IntoTheDeepCRIComparisonStats)?.specimen ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Sample", TELEOP_COLOR.chart) { (it.gameSpecific as? IntoTheDeepCRIComparisonStats)?.sample ?: EMPTY_COMPARISON_STAT },
        BoxPlotSeries("Navigation", ENDGAME_COLOR.chart) { (it.gameSpecific as? IntoTheDeepCRIComparisonStats)?.navigation ?: EMPTY_COMPARISON_STAT },
    ],
)

private val compareGrid = ClassName {
    height = 100.pct
    display = Display.grid
    gridTemplateColumns = 1.fr
    gridTemplateRows = array(3.em, 1.fr)
}

private val compareHeader = ClassName {
    padding = 0.5.em
    display = Display.flex
    gap = 1.em
    flexDirection = FlexDirection.row
    justifyContent = JustifyContent.spaceEvenly
}

private val teamInputWrapper = ClassName {
    border = Border(1.px, LineStyle.solid, NamedColor.white)
    borderRadius = 0.4.rem
    width = 10.em
    fontSize = 1.rem
    color = NamedColor.white

    "&:focus-within" {
        borderRadius = array(0.4.rem, 0.4.rem, 0.0.rem, 0.0.rem)
    }

    "input" {
        padding = 0.5.em
    }

    "[role=listbox]" {
        outline = Outline(1.px, LineStyle.solid, NamedColor.white)
    }
}

val EventCompareTeams = EventTabFC<API.Event.Compare, List<ComparisonStats>>({
    API.Event.Compare(it)
}) { (coroutineScope, year, eventCode, data) ->
    val searchParams = useSearch()
    val navigate = useNavigate()

    val teams = useMemo(data) {
        data?.map { TeamOption(it.number, it.name) }
    }
    val initial = useMemo(teams) {
        val (red, blue) = [searchParams["red"]?.toString(), searchParams["blue"]?.toString()].map {
            it?.split(",")?.mapNotNull { it.toIntOrNull() }?.take(3) ?: []
        }
        List<Int?>(3 - red.size) { null } + red + blue + List(3 - blue.size) { null }
    }
    val comparisonTeamsStates = List(6) { i -> useState { initial[i] } }

    val gameSpecific = GAME_SPECIFIC_SEARCH in searchParams

    val comparisonTeams = comparisonTeamsStates.map { (number, _) -> number }
    useEffect(*comparisonTeams.toTypedArray()) {
        if (null != data) {
            navigate {
                toCompareTeams(year, eventCode, comparisonTeams.take(3).filterNotNull(), comparisonTeams.drop(3).filterNotNull(), gameSpecific)
                replace = true
            }
        }
    }

    div {
        className = compareGrid

        div {
            className = compareHeader
            repeat(6) { i ->
                var team by comparisonTeamsStates[i]
                TeamSearch {
                    className = ClassName(teamInputWrapper) {
                        backgroundColor = (if (i < 3) AllianceColor.RED else AllianceColor.BLUE).backgroundColor
                    }
                    label = "${if (i < 3) "Red" else "Blue"} team ${i % 3 + 1}"
                    this.teams = teams
                    keepsSelection = true
                    selected = team
                    onSelect = {
                        team = it?.number
                    }
                }
            }
        }


        if (null != data) {
            BoxPlot {
                val teamStats = comparisonTeamsStates.map { (v, _) -> v }.map { number -> data.firstOrNull { it.number == number } }

                val redStats = teamStats.slice(0..<3).filterNotNull()
                val blueStats = teamStats.slice(3..<6).filterNotNull()

                val size = max(redStats.size, blueStats.size)

                val fullRedStats = List(size - redStats.size) { EMPTY_COMPARISON_STATS } + redStats
                val fullBlueStats = blueStats + List(size - blueStats.size) { EMPTY_COMPARISON_STATS }

                val combinedStats =
                    if (fullRedStats.size <= 1 || fullBlueStats.size <= 1) emptyList()
                    else [
                        "Red" to redStats, "Blue" to blueStats
                    ].map { (alliance, stats) ->
                        (stats.reduceOrNull(ComparisonStats::combine) ?: EMPTY_COMPARISON_STATS).copy(name = alliance, number = -1)
                    }


                this.data = fullRedStats + combinedStats + fullBlueStats
                series = GAME_SPECIFIC_CONFIGS[if (gameSpecific) year else -1] ?: GAME_SPECIFIC_CONFIGS[-1]!!
                onToggle = {
                    if (year in GAME_SPECIFIC_CONFIGS) {
                        navigate {
                            toCompareTeams(
                                year, eventCode,
                                comparisonTeams.take(3).filterNotNull(), comparisonTeams.drop(3).filterNotNull(),
                                !gameSpecific,
                            )
                            replace = true
                        }
                    }
                }
                onClick = {
                    navigate {
                        toTeam(year, it.number, eventCode)
                    }
                }
            }
        }
    }
}


private data class BoxPlotSeries(val title: String, val color: Color, val stat: (ComparisonStats) -> ComparisonStat)

private external interface BoxPlotProps : Props {
    var data: List<ComparisonStats>
    var series: List<BoxPlotSeries>
    var onToggle: () -> Unit
    var onClick: ((ComparisonStats) -> Unit)?
}

private val BoxPlot = FC<BoxPlotProps>("BoxPlot") { props ->
    var touch by useState(false)

    div {
        css {
            overflow = Overflow.hidden
            ".recharts-rectangle.recharts-tooltip-cursor" {
                this[Selector("fill")] = "#00000049"
            }
        }

        val EventCompareTeamsTooltip = FC("EventCompareTeamsTooltip") { propsTooltip: dynamic ->
            if (!propsTooltip["active"].unsafeCast<Boolean>()) return@FC
            val payload = propsTooltip["payload"][0]
            if (undefined == payload) return@FC
            val instance = payload["payload"].unsafeCast<ComparisonStats>()
            if (0 == instance.number) return@FC
            div {
                css {
                    opacity = number(0.9)
                    background = BACKGROUND_COLOR
                    border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
                    borderRadius = 4.px
                    color = NamedColor.white
                    padding = array(0.0.em, 0.5.em)

                    if (touch) pointerEvents = Auto.auto

                    ".label" {
                        fontWeight = FontWeight.bold
                    }
                }
                if (instance.number >= 1) {
                    onPointerDown = { event ->
                        if (touch) props.onClick?.invoke(instance)
                    }
                }
                p {
                    className = ClassName("label")
                    if (instance.number >= 1) {
                        css {
                            textDecoration = TextDecoration.underline
                        }
                    }
                    b {
                        +if (instance.number >= 1) "${instance.number} - ${instance.name}" else instance.name
                    }
                }
                props.series.forEach { series ->
                    p {
                        className = ClassName("intro")
                        +"${series.title}: ${series.stat(instance).oprn.toFixed(1)}"
                    }
                }
            }
        }

        val charWidth = 0.7 * getComputedStyle(document.documentElement).fontSize.dropLast(2).toDouble()
        val maxKeyChars = (window.innerWidth / props.data.size / charWidth).toInt()

        onPointerDown = { event -> touch = PointerType.mouse != event.pointerType }
        ResponsiveContainer {
            width = 100.pct
            height = 100.pct
            BarChart {
                onClick = { event ->
                    if (!touch) {
                        val payload = event["activePayload"]
                        if (undefined != payload) {
                            val instance = payload[0]["payload"].unsafeCast<ComparisonStats>()
                            if (instance.number >= 1) props.onClick?.invoke(instance)
                        }
                    }
                }

                margin = ChartMargin(20, 25, 15, 15)

                data = props.data.toTypedArray()

                CartesianGrid {
                    strokeDasharray = "6 3"
                    vertical = false
                }
                XAxis {
                    dataKey = { instance: ComparisonStats ->
                        if (instance.name.length <= maxKeyChars) instance.name else instance.name.take(maxKeyChars - 3) + "..."
                    }
                    type = AxisType.category
                    interval = 0
                    angle = 0
                    height = 40
                    tick = unsafeJso {
                        this["dx"] = 0
                        this["dy"] = 15
                    }
                }
                YAxis {
                    tickCount = 10
                    Label {
                        value = "OPRN"
                        position = ComponentPosition.insideLeft
                        angle = -90
                        style = unsafeJso {
                            textAnchor = "middle"
                        }
                    }
                }
                Legend {
                    layout = LayoutDirection.horizontal
                    onClick = { props.onToggle() }
                }

                fun plotComparisonStat(title: String, color: Color, stat: (ComparisonStats) -> ComparisonStat) {
                    Bar {
                        // Min
                        stackId = title
                        fill = "none"
                        dataKey = { instance: ComparisonStats ->
                            stat(instance).min
                        }
                        legendType = LegendType.none
                    }

                    Bar {
                        stackId = title
                        shape = HorizonBar
                        legendType = LegendType.none
                    }

                    Bar {
                        // (OPR - StdDev) - Min
                        stackId = title
                        shape = WhiskerBar
                        dataKey = { instance: ComparisonStats ->
                            stat(instance).oprn - stat(instance).stdev - stat(instance).min
                        }
                        legendType = LegendType.none
                    }

                    Bar {
                        // StdDev
                        name = title
                        stackId = title
                        fill = color.toString()

                        dataKey = { instance: ComparisonStats ->
                            stat(instance).stdev
                        }
                    }

                    Bar {
                        stackId = title
                        shape = HorizonBar
                        legendType = LegendType.none
                    }

                    Bar {
                        // StdDev
                        stackId = title
                        fill = color.toString()
                        dataKey = { instance: ComparisonStats ->
                            stat(instance).stdev
                        }
                        legendType = LegendType.none
                    }

                    Bar {
                        // Max - (OPR + StdDev)
                        stackId = title
                        shape = WhiskerBar
                        dataKey = { instance: ComparisonStats ->
                            stat(instance).max - (stat(instance).oprn + stat(instance).stdev)
                        }
                        legendType = LegendType.none
                    }

                    Bar {
                        stackId = title
                        shape = HorizonBar
                        legendType = LegendType.none
                    }

                    Tooltip {
                        content = EventCompareTeamsTooltip
                    }
                }

                props.series.forEach { series ->
                    plotComparisonStat(series.title, series.color, series.stat)
                }
            }
        }
    }
}


external interface RectProps : Props {
    var x: Int
    var y: Int
    var width: Int
    var height: Int
}

private val HorizonBar = FC<RectProps>("HorizonBar") { props ->
    svg {
        line {
            x1 = props.x.toDouble()
            y1 = props.y.toDouble()
            x2 = props.x + props.width.toDouble()
            y2 = props.y.toDouble()
            stroke = "#fff"
            strokeWidth = 3.0
        }
    }
}

private var WhiskerBar = FC<RectProps>("WhiskerBar") { props ->
    svg {
        line {
            x1 = props.x + props.width / 2.0
            y1 = props.y + props.height.toDouble()
            x2 = props.x + props.width / 2.0
            y2 = props.y.toDouble()
            stroke = "#fff"
            strokeWidth = 5.0
        }
    }
}

private infix fun ComparisonStats.combine(other: ComparisonStats) = ComparisonStats(
    0,
    "",
    npTotal combine other.npTotal,
    auto combine other.auto,
    teleop combine other.teleop,
    endgame combine other.endgame,
    gameSpecific combine other.gameSpecific,
)

private infix fun GameSpecificComparisonStats?.combine(other: GameSpecificComparisonStats?) = when {
    this is IntoTheDeepComparisonStats && other is IntoTheDeepComparisonStats -> this combine other
    this is IntoTheDeepCRIComparisonStats && other is IntoTheDeepCRIComparisonStats -> this combine other
    else -> null
}

private infix fun IntoTheDeepComparisonStats.combine(other: IntoTheDeepComparisonStats) = IntoTheDeepComparisonStats(
    specimen combine other.specimen,
    sample combine other.sample,
    navigation combine other.navigation,
)

private infix fun IntoTheDeepCRIComparisonStats.combine(other: IntoTheDeepCRIComparisonStats) = IntoTheDeepCRIComparisonStats(
    specimen combine other.specimen,
    sample combine other.sample,
    navigation combine other.navigation,
)

private infix fun ComparisonStat.combine(other: ComparisonStat) = ComparisonStat(
    oprn + other.oprn,
    hypot(stdev, other.stdev),
    min + other.min,
    max + other.max,
)
